import type { LimitDetail } from '@gonfalon/billing/types';
import { ActivityResourceFiltersOptions } from '@gonfalon/change-history/ResourceFilters/ActivityResourceFilters';
import type { ResourceType as GonfalonResourceType } from '@gonfalon/resource-specifiers/ast';
import { subDays } from 'date-fns';
import { type CustomRole as CustomRoleFromApi } from 'launchdarkly-api-typescript';
import numbro from 'numbro';

import { type ILinks, type Link } from '../gonfalon';

export const UNLIMITED = -1;
export const NOT_SET = -2;
export const MAX_RECENTLY_VIEWED_ACCOUNTS = 20;
const MAX_PROJECT_LIMIT = 2;
const MAX_ENVIRONMENT_LIMIT = 4;

type AccountLinks = Readonly<{
  self: Link;
  subscription: Link;
  members: Link;
  projects: Link;
  customRoles: Link;
}>;

export type Account = Readonly<{
  _links: AccountLinks;
  _id: string;
  organization?: string;
  ownerEmail: string;
  tokens?: readonly string[];
  samlConfig?: SamlConfig;
  requireMfa: boolean;
  billingContact: BillingContact;
  // this is only used in search
  subscription?: AccountListingSubscription;
  isSupportGenAiEnabled: boolean;
  // There is no `enableAccountImpersonation` boolean: the backend only ever
  // sends the expiry (AccountRep.EnableAccountImpersonationUntil), and enhanced
  // support is "on" while that timestamp is in the future.
  enableAccountImpersonationUntil?: number;
  sessionIpAllowlistEnabled?: boolean;
  apiTokenIpAllowlistEnabled?: boolean;
}>;

export type BillingContact = {
  name?: string;
  email?: string;
  company?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  poNumber?: string;
};

export type OwnerPromotion = Readonly<{
  requestedBy: string;
  promotedEmail: string;
  demotedRole: string;
}>;

export type AuditLogEntries = Readonly<{
  entries: AuditLogEntry[];
  items: AuditLogEntry[];
  _links: ILinks;
  isFetching: boolean;
  // True while the rows on screen still belong to the previous search.
  isPlaceholderData?: boolean;
  loadMore?: () => void;
  // Fetches and inserts the entries immediately newer than the top of the
  // list, keeping it contiguous. The list keeps its scroll position.
  // `complete` means the newest entry has now been reached. `partial` means
  // more remain. `empty` means nothing was inserted, which includes the case
  // where no range could be resolved.
  loadNewer?: () => Promise<'empty' | 'partial' | 'complete'>;
}>;

export type AuditLogEntry = Readonly<{
  _id: string;
  date: number;
  accesses: AuditLogAccesses[];
  description: string;
  kind: string;
  member?: AuditLogMember;
  subject?: AuditLogSubject;
  app?: AuditLogApp;
  name: string;
  parent?: AuditLogParent;
  title: string;
  titleVerb: string;
  token?: AuditLogToken;
  _links: ILinks;
}>;

export type AuditLogEntryDetail = Readonly<{
  isFetching?: boolean;
  _id: string;
  date: number;
  accesses: AuditLogAccesses[];
  description: string;
  comment?: string;
  kind: string;
  member?: AuditLogMember;
  subject?: AuditLogSubject;
  app?: AuditLogApp;
  name: string;
  parent?: AuditLogParent;
  title: string;
  titleVerb: string;
  token?: AuditLogToken;
  delta?: AuditLogEntryDelta[];
  previousVersion?: object;
  currentVersion?: object;
}>;

export type AuditLogMember = Readonly<{
  email: string;
  firstName?: string;
  lastName?: string;
}>;

export type AuditLogParent = Readonly<{
  name: string;
  resource: string;
}>;

export type AuditLogToken = Readonly<{
  name: string;
  ending: string;
  serviceToken?: boolean;
}>;

export type AuditLogSubject = Readonly<{
  name: string;
}>;

export type AuditLogApp = Readonly<{
  name: string;
  isScim: boolean;
}>;

export type AuditLogAccesses = Readonly<{
  action: unknown;
  resource: string;
}>;

export type AuditLogEntryDelta = Readonly<{
  op: string;
  path: string;
  value: unknown;
}>;

// Use ResourceType from gonfalon's resource-specifiers package
// Extended with vitalstatistix-specific value for "no selection"
export type ResourceType = GonfalonResourceType | 'noResourceSelected';

// Export const value for "no selection" state
export const NO_RESOURCE_SELECTED = 'noResourceSelected' as const;

// Build readable names from ActivityResourceFiltersOptions (shared with other apps)
// and add vitalstatistix-specific resource names
export const ResourcesReadableNames: Partial<Record<GonfalonResourceType, string>> &
  Record<typeof NO_RESOURCE_SELECTED, string> = {
  noResourceSelected: 'no resource selected',
  // Import names from the shared ActivityResourceFiltersOptions
  ...Object.fromEntries(ActivityResourceFiltersOptions.map((option) => [option.value, option.name])),
  // Vitalstatistix-specific resource types not in ActivityResourceFiltersOptions
  'ai-tool': 'AI Tool',
  destination: 'Destination',
  'pending-request': 'Pending request',
  token: 'Token',
  version: 'Application version',
};

export const ResourcesWrittenExpressions: Partial<Record<GonfalonResourceType, string>> &
  Record<typeof NO_RESOURCE_SELECTED, string> = {
  noResourceSelected: '',
  acct: 'acct',
  'ai-model-config': 'proj/*:ai-model-config/*',
  'ai-tool': 'proj/*:ai-tool/*',
  aiconfig: 'proj/*:env/*:aiconfig/*',
  alert: 'proj/*:alert/*',
  application: 'application/*',
  'code-reference-repository': 'code-reference-repository/*',
  'context-kind': 'proj/*:context-kind/*',
  destination: 'proj/*:env/*:destination/*',
  env: 'proj/*:env/*',
  experiment: 'proj/*:env/*:experiment/*',
  flag: 'proj/*:env/*:flag/*',
  holdout: 'proj/*:env/*:holdout/*',
  integration: 'integration/*',
  layer: 'proj/*:layer/*',
  member: 'member/*',
  metric: 'proj/*:metric/*',
  'metric-group': 'proj/*:metric-group/*',
  'multi-armed-bandit': 'proj/*:env/*:multi-armed-bandit/*',
  'observability-dashboard': 'proj/*:observability-dashboard/*',
  'observability-settings': 'proj/*:observability-settings/*',
  'payload-filter': 'proj/*:payload-filter/*',
  'pending-request': 'pending-request/*',
  proj: 'proj/*',
  'relay-proxy-config': 'relay-proxy-config/*',
  'release-pipeline': 'proj/*:release-pipeline/*',
  'release-policy': 'proj/*:release-policy/*',
  role: 'role/*',
  'sdk-key': 'proj/*:env/*:sdk-key/*',
  segment: 'proj/*:env/*:segment/*',
  'service-token': 'service-token/*',
  team: 'team/*',
  template: 'template/*',
  token: 'member/*:token/*',
  version: 'application/*:version/*',
  view: 'proj/*:view/*',
  webhook: 'webhook/*',
};

export type SamlConfig = Readonly<{
  enabled: boolean;
  requireSso: boolean;
  ssoUrl: string;
  idpEntityId?: string;
}>;

export enum NoAccountsMsg {
  DEFAULT = 'default',
  FAVORITED = 'favorited',
  RECENTLY_VIEWED = 'recentlyViewed',
}

export type SavedAccount = Readonly<{
  id: string;
  displayName: string;
}>;

export enum MFAStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  RESET = 'reset',
}

export enum SDKType {
  SERVER = 'server',
  CLIENT = 'client',
}

export enum ChartTypes {
  BAR = 'bar',
  LINE = 'line',
  AREA = 'events',
}

export type MembersPaginationData = Readonly<{
  links: {
    self: { href: string };
    first: { href: string };
    last: { href: string };
    next: { href: string };
    prev: { href: string };
  };
  totalCount: number;
}>;

export type MemberResponse = Readonly<{
  items: Member[];
  totalCount: number;
  _links: {
    self: { href: string; type: string };
    [key: string]: { href: string; type: string };
  };
}>;

export type Member = Readonly<{
  _id: string;
  _pendingInvite: boolean;
  email: string;
  firstName?: string;
  lastName?: string;
  role: AccountRole;
  customRoles: readonly string[];
  mfa: MFAStatus;
  mfaEnforced?: boolean;
  mfaGracePeriodExpiresAt?: number;
  hasPassword: boolean;
  oauthProviders?: readonly string[];
  scimExternalId?: string;
  teams: readonly TeamsData[];
  roleAttributes?: RoleAttributeMap;
  _lastSeen?: number;
}>;

export type TeamsData = Readonly<{
  key: string;
  name: string;
  customRoleKeys: readonly string[];
}>;

export type MemberSessions = Readonly<{
  memberId: string;
  lastActiveDate: string;
  authKind: string;
}>;

export type Environment = Readonly<{
  _id: string;
  key: string;
  name: string;
  color: string;
  // Withheld by the API when the caller may not see the SDK keys.
  apiKey?: string;
  mobileKey?: string;
}>;

export type Project = Readonly<{
  _id: string;
  key: string;
  name: string;
  // API v20240415 inlines at most the first 20 environments; the rest are fetched on demand.
  environments: readonly Environment[];
  environmentsTotalCount: number;
}>;

/** A hit from the account-wide environment search, with the project it belongs to. */
export type EnvironmentSummary = Environment &
  Readonly<{
    projectKey: string;
    projectName: string;
  }>;

export type CustomRole = Readonly<CustomRoleFromApi>;

export type RoleAttributeMap = Readonly<{
  [key: string]: string[];
}>;

export type LegacyPlanLimits = Readonly<{
  multipleProjects: boolean;
  multipleEnvironments: boolean;
  abTesting: boolean;
  teams: boolean;
  customRoles: boolean;
  auditLog: boolean;
  mauLimit: number;
  enforceSeatLimits: boolean;
}>;

export type LegacyPlanUsage = Readonly<{
  mauCount: {
    total: number;
    anonymous: number;
    nonAnonymous: number;
    client: number;
    server: number;
  };
  projectCount: number;
  memberCount: number;
  environmentCount: number;
  abTesting: boolean;
}>;

export type LegacySubscription = Readonly<{
  _limits: LegacyPlanLimits;
  _usage: LegacyPlanUsage;
  trialStartDate: string;
  trialEndDate: string;
  graceStartDate?: string;
  graceEndDate?: string;
  periodStartDate?: string;
  periodEndDate?: string;
  seatCount?: number;
  isBeta: boolean;
  isTrial: boolean;
  isActive: boolean;
  isCurrent: boolean;
  isCanceled: boolean;
  name: string | null;
}>;

export enum AccountRole {
  ADMIN = 'admin',
  WRITER = 'writer',
  READER = 'reader',
  OWNER = 'owner',
}

export enum BillingInterval {
  ANNUAL_BILLING = 'AnnualBilling',
  MONTHLY_BILLING = 'MonthlyBilling',
}

export enum PlanType {
  STARTER = 'Starter',
  STANDARD_TRIAL = 'StandardTrial',
  PROFESSIONAL = 'Professional',
  ENTERPRISE_TRIAL = 'EnterpriseTrial',
  ENTERPRISE = 'Enterprise',
  LEGACY = 'Legacy',
  STANDARD_TRIAL_2021 = 'StandardTrial2021',
  STARTER_2021 = 'Starter2021',
  PROFESSIONAL_2021 = 'Professional2021',
  FOUNDATION_2023 = 'Foundation2023',
  ENTERPRISE_2023 = 'Enterprise2023',
  FOUNDATION_TRIAL_2023 = 'FoundationTrial2023',
  GUARDIAN_TRIAL_2024 = 'GuardianTrial2024',
  GUARDIAN_2024 = 'Guardian2024',
  GUARDIAN_SEATS_2024 = 'Guardian2024S',
  DEVELOPER_2025 = 'Developer2025',
  FOUNDATION_2025 = 'Foundation2025',
}

export const isFixedPlan = (planType: PlanType) =>
  planType === PlanType.STARTER ||
  planType === PlanType.STANDARD_TRIAL ||
  planType === PlanType.ENTERPRISE_TRIAL ||
  planType === PlanType.STANDARD_TRIAL_2021 ||
  planType === PlanType.FOUNDATION_TRIAL_2023 ||
  planType === PlanType.GUARDIAN_TRIAL_2024 ||
  planType === PlanType.DEVELOPER_2025;

export const isSelfServePlan = (planType: PlanType) =>
  planType === PlanType.STARTER ||
  planType === PlanType.PROFESSIONAL ||
  planType === PlanType.STARTER_2021 ||
  planType === PlanType.PROFESSIONAL_2021 ||
  planType === PlanType.FOUNDATION_2023 ||
  planType === PlanType.FOUNDATION_TRIAL_2023 ||
  planType === PlanType.DEVELOPER_2025 ||
  planType === PlanType.FOUNDATION_2025;

// Returns true if the plan type supports toggling the "free" flag in the UI
// Self-serve plans generally support this, except:
// - Developer2025: always free (fixed plan)
// - Foundation2025: always paid (UBB plan)
export const supportsFreeToggle = (planType: PlanType): boolean => {
  if (!isSelfServePlan(planType)) {
    return false;
  }

  // Hide toggle for 2025 plans: Developer2025 is always free, Foundation2025 is always paid
  const is2025Plan = planType === PlanType.DEVELOPER_2025 || planType === PlanType.FOUNDATION_2025;
  return !is2025Plan;
};

// Returns true if the plan type has billing interval options (monthly/annual)
// Trial plans and Developer2025 (free fixed plan) don't have billing intervals
export const hasBillingInterval = (planType: PlanType): boolean =>
  planType !== PlanType.STANDARD_TRIAL &&
  planType !== PlanType.ENTERPRISE_TRIAL &&
  planType !== PlanType.STANDARD_TRIAL_2021 &&
  planType !== PlanType.FOUNDATION_TRIAL_2023 &&
  planType !== PlanType.GUARDIAN_TRIAL_2024 &&
  planType !== PlanType.DEVELOPER_2025;

export const isHostBasedPlan = (planType: PlanType) =>
  planType === PlanType.ENTERPRISE_2023 ||
  planType === PlanType.FOUNDATION_2023 ||
  planType === PlanType.FOUNDATION_2025 ||
  planType === PlanType.GUARDIAN_2024;

export const isEnterprisePlan = (planType: PlanType) =>
  planType === PlanType.ENTERPRISE || planType === PlanType.ENTERPRISE_2023;

export const isGuardianPlan = (planType: PlanType) =>
  planType === PlanType.GUARDIAN_2024 || planType === PlanType.GUARDIAN_SEATS_2024;

export const isTrialPlan = (planType: PlanType) =>
  planType === PlanType.STANDARD_TRIAL ||
  planType === PlanType.ENTERPRISE_TRIAL ||
  planType === PlanType.STANDARD_TRIAL_2021 ||
  planType === PlanType.FOUNDATION_TRIAL_2023 ||
  planType === PlanType.GUARDIAN_TRIAL_2024;

// Helper function to format plan type with spaces (consistent with subscription state formatting)
export const formatPlanType = (planType: string): string =>
  planType
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between camelCase words
    .replace(/([a-zA-Z])(\d)/g, '$1 $2') // Add space before numbers
    .replace(/[_-]/g, ' ') // Replace underscores and dashes with spaces
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const PlanTypeReadableNames: { [key in PlanType]: string } = {
  [PlanType.STARTER]: formatPlanType(PlanType.STARTER),
  [PlanType.STANDARD_TRIAL]: formatPlanType(PlanType.STANDARD_TRIAL),
  [PlanType.PROFESSIONAL]: formatPlanType(PlanType.PROFESSIONAL),
  [PlanType.ENTERPRISE_TRIAL]: formatPlanType(PlanType.ENTERPRISE_TRIAL),
  [PlanType.ENTERPRISE]: formatPlanType(PlanType.ENTERPRISE),
  [PlanType.LEGACY]: formatPlanType(PlanType.LEGACY),
  [PlanType.STANDARD_TRIAL_2021]: formatPlanType(PlanType.STANDARD_TRIAL_2021),
  [PlanType.STARTER_2021]: formatPlanType(PlanType.STARTER_2021),
  [PlanType.PROFESSIONAL_2021]: formatPlanType(PlanType.PROFESSIONAL_2021),
  [PlanType.FOUNDATION_2023]: formatPlanType(PlanType.FOUNDATION_2023),
  [PlanType.ENTERPRISE_2023]: formatPlanType(PlanType.ENTERPRISE_2023),
  [PlanType.FOUNDATION_TRIAL_2023]: formatPlanType(PlanType.FOUNDATION_TRIAL_2023),
  [PlanType.GUARDIAN_TRIAL_2024]: formatPlanType(PlanType.GUARDIAN_TRIAL_2024),
  [PlanType.GUARDIAN_2024]: formatPlanType(PlanType.GUARDIAN_2024),
  [PlanType.GUARDIAN_SEATS_2024]: 'Guardian Seats 2024',
  [PlanType.DEVELOPER_2025]: formatPlanType(PlanType.DEVELOPER_2025),
  [PlanType.FOUNDATION_2025]: formatPlanType(PlanType.FOUNDATION_2025),
};

// Plan versions for Foundation2023. The integer value maps to the Gonfalon plan
// version (v1-v4), while the label describes the product flavor surfaced to admins.
export enum Foundation2023PlanVersion {
  V1 = 1,
  V2 = 2,
  V3 = 3,
  V4 = 4,
}

export const Foundation2023PlanVersionReadableNames: { [key in Foundation2023PlanVersion]: string } = {
  [Foundation2023PlanVersion.V1]: 'Feature management (v1)',
  [Foundation2023PlanVersion.V2]: 'FM + o11y (v2)',
  [Foundation2023PlanVersion.V3]: 'Code + agent control (v3)',
  [Foundation2023PlanVersion.V4]: 'Agent control only (v4)',
};

// Returns true if the plan type supports selecting a Foundation2023 plan version
export const supportsPlanVersionSelection = (planType: PlanType): boolean => planType === PlanType.FOUNDATION_2023;

// TODO: Replace with LimitNames
export enum MetricType {
  SEATS = 'SeatCount',
  SERVER_CONNECTIONS = 'ServerConnections',
  MONTHLY_ACTIVE_USERS = 'MonthlyActiveUsers',
  EVENTS_RECEIVED = 'EventsReceived',
  EXPERIMENTATION_KEYS = 'ExperimentationKeys',
  EXPERIMENTATION_MAU = 'ExperimentationMAU',
  EVENTS_PUBLISHED = 'EventsPublished',
  SSO = 'SSO',
  HOSTS = 'Hosts',
  AI_SERVICE_CONNECTIONS = 'AIServiceConnections',
  ANALYTICS_MAU = 'AnalyticsMAU',
  ERRORS = 'Errors',
  LOGS = 'Logs',
  SESSIONS = 'Sessions',
  TRACES = 'Traces',
  AI_RUNS = 'AIRuns',
  VEGA_AI = 'VegaAI',
}

export type SubscriptionUsage = Readonly<{
  _usage: AccountMetrics;
  _links: ILinks;
}>;

export type AccountMetrics = Readonly<{
  [MetricType.SEATS]: number;
  [MetricType.SERVER_CONNECTIONS]: number;
  [MetricType.MONTHLY_ACTIVE_USERS]: number;
  [MetricType.EVENTS_RECEIVED]: number;
  [MetricType.EXPERIMENTATION_KEYS]: number;
  [MetricType.EXPERIMENTATION_MAU]: number;
  [MetricType.EVENTS_PUBLISHED]: number;
  [MetricType.SSO]: number;
  [MetricType.HOSTS]: number;
  [MetricType.AI_SERVICE_CONNECTIONS]: number;
  [MetricType.ANALYTICS_MAU]: number;
  [MetricType.ERRORS]: number;
  [MetricType.LOGS]: number;
  [MetricType.SESSIONS]: number;
  [MetricType.TRACES]: number;
  [key: string]: number;
}>;

// Default limits for Developer2025 plan (fallback when switching plans)
// These match the backend LaunchDarkly flags for Developer2025
// When editing an existing Developer2025 account, we use sub._limits which has the evaluated values
export const developer2025DefaultLimits: Partial<AccountMetrics> = {
  [MetricType.HOSTS]: 5,
  [MetricType.MONTHLY_ACTIVE_USERS]: 1000,
  [MetricType.EXPERIMENTATION_MAU]: 100000,
  [MetricType.ERRORS]: 5000,
  [MetricType.LOGS]: 10000000,
  [MetricType.TRACES]: 10000000,
  [MetricType.SESSIONS]: 5000,
};

// Default limits for Foundation2025 plan
// These provide reasonable starting values for new Foundation2025 subscriptions
export const foundation2025DefaultLimits: Partial<AccountMetrics> = {
  [MetricType.HOSTS]: 8,
  [MetricType.MONTHLY_ACTIVE_USERS]: 1000,
  [MetricType.ERRORS]: 10000,
  [MetricType.LOGS]: 50000000,
  [MetricType.TRACES]: 50000000,
  [MetricType.SESSIONS]: 10000,
};

export enum SubscriptionState {
  ACTIVE_SUBSCRIPTION = 'ActiveSubscription',
  ACTIVE_TRIAL = 'ActiveTrial',
  ACTIVE_TRIAL_WITH_SUBSCRIPTION = 'ActiveTrialWithSubscription',
  GRACE_PERIOD_TRIAL = 'GracePeriodTrial',
  LAPSED_TRIAL = 'LapsedTrial',
  FAILED_PAYMENT_SUBSCRIPTION = 'FailedPaymentSubscription',
  PENDING_CANCELED_SUBSCRIPTION = 'PendingCanceledSubscription',
  CANCELED_SUBSCRIPTION = 'CanceledSubscription',
  LAPSED_SUBSCRIPTION = 'LapsedSubscription',
  USAGE_EXCEEDED_GRACE_PERIOD = 'UsageExceededGracePeriod',
  USAGE_EXCEEDED_READ_ONLY = 'UsageExceededReadOnly',
}

export const hasSubscription = (subscription: Subscription) =>
  subscription.state === SubscriptionState.ACTIVE_SUBSCRIPTION ||
  subscription.state === SubscriptionState.ACTIVE_TRIAL_WITH_SUBSCRIPTION ||
  subscription.state === SubscriptionState.FAILED_PAYMENT_SUBSCRIPTION ||
  subscription.state === SubscriptionState.LAPSED_SUBSCRIPTION ||
  subscription.state === SubscriptionState.USAGE_EXCEEDED_GRACE_PERIOD ||
  subscription.state === SubscriptionState.USAGE_EXCEEDED_READ_ONLY;

export const SubscriptionStateDescriptions: { [key in SubscriptionState]: string } = {
  ActiveTrial:
    'Subscription is in the trial period and can be canceled or not (if you cancel while on trial, that trial should continue until the end)',
  ActiveTrialWithSubscription: 'Subscription has a plan but is still in the trial period and is not canceled',
  GracePeriodTrial: 'Subscription is a trial but no longer is in the trial period and is not canceled',
  LapsedTrial: 'Subscription is a trial but is canceled or past the grace period',
  ActiveSubscription:
    'Subscription is a paid plan type, not canceled, not on a trial, and has an unenforced grace period.',
  FailedPaymentSubscription: 'Subscription is a paid plan type that is not canceled and is in an active grace period.',
  PendingCanceledSubscription: 'Subscription is canceled. Has an active trial or active grace period.',
  CanceledSubscription:
    'Subscription is a paid plan type, is not in the grace period, and is canceled. Has a lapsed grace period or if canceled an unenforced grace period.',
  LapsedSubscription:
    'Subscription is a paid plan type not in the trial period. Is not canceled but has a lapsed grace period.',
  UsageExceededGracePeriod: 'Subscription has exceeded usage and is in a grace period.',
  UsageExceededReadOnly: 'Subscription has exceeded usage and is in a read-only state.',
};

// Helper function to format subscription state with spaces (consistent with omnibar)
export const formatSubscriptionState = (state: string): string =>
  state
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between camelCase words
    .replace(/[_-]/g, ' ') // Replace underscores and dashes with spaces
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const SubscriptionStateReadableNames: { [key in SubscriptionState]: string } = {
  ActiveTrial: formatSubscriptionState(SubscriptionState.ACTIVE_TRIAL),
  ActiveTrialWithSubscription: formatSubscriptionState(SubscriptionState.ACTIVE_TRIAL_WITH_SUBSCRIPTION),
  GracePeriodTrial: formatSubscriptionState(SubscriptionState.GRACE_PERIOD_TRIAL),
  LapsedTrial: formatSubscriptionState(SubscriptionState.LAPSED_TRIAL),
  ActiveSubscription: formatSubscriptionState(SubscriptionState.ACTIVE_SUBSCRIPTION),
  FailedPaymentSubscription: formatSubscriptionState(SubscriptionState.FAILED_PAYMENT_SUBSCRIPTION),
  PendingCanceledSubscription: formatSubscriptionState(SubscriptionState.PENDING_CANCELED_SUBSCRIPTION),
  CanceledSubscription: formatSubscriptionState(SubscriptionState.CANCELED_SUBSCRIPTION),
  LapsedSubscription: formatSubscriptionState(SubscriptionState.LAPSED_SUBSCRIPTION),
  UsageExceededGracePeriod: formatSubscriptionState(SubscriptionState.USAGE_EXCEEDED_GRACE_PERIOD),
  UsageExceededReadOnly: formatSubscriptionState(SubscriptionState.USAGE_EXCEEDED_READ_ONLY),
};

export enum TrialFeatures {
  EXPERIMENTATION_KEY = 'account-limits-experiment-events',
}

type CampaignType = {
  startDate: string;
  endDate: string;
  extensionCount: number;
};

export type Subscription = Readonly<{
  _limits: AccountMetrics;
  _limitDetails?: readonly LimitDetail[];
  state: SubscriptionState;
  planType: PlanType;
  version: number;
  trialStartDate: string;
  trialEndDate: string;
  usagePeriodStartDate: string;
  usagePeriodEndDate: string;
  periodStartDate?: string;
  periodEndDate?: string;
  graceStartDate?: string;
  graceEndDate?: string;
  readOnlyStartDate?: string;
  pendingUpdate?: boolean;
  stripeSubscriptionId?: string;
  orbSubscriptionId?: string;
  trialExtensionCount: number;
  subscriptionType?: string;
  marketplaceProvider?: string;
  billingInterval?: string;
  enforceSeatLimits?: boolean;
  campaigns: {
    CustomerEnterpriseCampaign: CampaignType;
    CustomerGuardianCampaign: CampaignType;
  };
}>;

export type AccountListingSubscription = Readonly<{
  state: SubscriptionState;
  type: string;
  planType?: PlanType;
}>;

export type Plan = Readonly<{
  planType: PlanType;
  // Only Foundation2023 / FoundationTrial2023 plans carry a version; constrained
  // to the known versions so out-of-range values are caught at compile time.
  version?: Foundation2023PlanVersion;
  free: boolean;
  limits: AccountMetrics;
  effectiveStartDate: number;
  prorationTime: number;
  billingInterval: BillingInterval;
  featureTrial: TrialFeatures;
}>;

export enum CatfoodProjectKeys {
  LAUNCH_DARKLY = 'launchdarkly',
  ACCOUNT_MAGIC = 'account-magic',
}

export enum CatfoodFlagKeys {
  ENFORCE_SEAT_LIMITS = 'enforce-seat-limits',
}

/**
 * One account limit that has been raised above its live flag default (a genuine
 * increase), as returned in the support-internal get-general response (TSS-1181).
 * Used for both resource-count limits (`resourceLimits`) and numeric API/rate
 * limits (`rateLimitIncreases`). The `default` is read live from the flag by the
 * lambda, never hardcoded. UNLIMITED (-1) renders as ∞.
 */
export type AccountLimitIncrease = Readonly<{
  flagKey: string;
  name: string;
  value: number;
  default: number;
}>;

/**
 * A single account-scoped API rate-limit increase (TSS-1181): a targeting
 * rule or individual target on the per-route rate-limit flag
 * (`api-rate-limit-route-ten-sec-new`) that support added for this account,
 * as surfaced by the support-internal get-general lambda. `description` is the
 * rule name (usually the customer + ticket link + any "(Temp)"/expiry note),
 * `rateLimit` is the served limit in req/10s (-1 = rate limiting disabled), and
 * `routes`/`methods` are the API routes and HTTP methods the grant applies to
 * (empty for a blanket individual-target exemption).
 */
export type ApiRateLimitIncrease = Readonly<{
  description: string;
  rateLimit: number | null;
  routes: readonly string[];
  methods: readonly string[];
}>;

export type Promotion = Readonly<{
  requestedBy: string;
  promotedEmail: string;
  demotedRole: string;
}>;

export const getAccountDisplayName = (acct: Account) => acct.organization || acct.ownerEmail || acct._id;
export const hasName = (member: Member) => member.firstName && member.lastName;

export const getMemberDisplayName = (member: Member) => {
  if (member.firstName && member.lastName) {
    return `${member.firstName} ${member.lastName}`;
  }
  return member.email;
};

export const isOwner = (member: Member) => member.role === AccountRole.OWNER;

export const getLegacyMAUPercentage = (sub: LegacySubscription) => sub._usage.mauCount.client / sub._limits.mauLimit;
export const getUsagePercentage = (subscriptionUsage: number, subLimit: number) =>
  subLimit === UNLIMITED ? 0 : subscriptionUsage / subLimit;

export const getDefaultDateRangeForHighUsage = () => {
  const start = subDays(new Date(), 14);
  const now = new Date();

  return {
    from: start.valueOf(),
    to: now.valueOf(),
  };
};

export const computeLimitRatio = (usage: number, limit: number) => (limit === -1 ? Infinity : usage / limit);

export const getProjectLimit = (limits: LegacyPlanLimits) => (limits.multipleProjects ? -1 : MAX_PROJECT_LIMIT);

export const getEnvironmentLimit = (limits: LegacyPlanLimits) =>
  limits.multipleEnvironments ? -1 : MAX_ENVIRONMENT_LIMIT;

export const formatLimit = (limit: number) => (limit === -1 ? '∞' : limit);

export const formatLimitAsString = (limit: number | undefined) => {
  switch (limit) {
    case undefined:
    case null:
      return '0';
    case -1:
      return '∞';
    default:
      return numbro(limit).format({ thousandSeparated: true });
  }
};

export const getLegacySeatLimit = (sub: LegacySubscription) => {
  if ('seatCount' in sub) {
    return sub.seatCount;
  }
  if (sub._limits.teams) {
    return UNLIMITED;
  }
  return 1;
};

export const isPlanWithObservabilityProducts = (planType: PlanType) =>
  planType === PlanType.ENTERPRISE ||
  planType === PlanType.ENTERPRISE_2023 ||
  planType === PlanType.GUARDIAN_2024 ||
  planType === PlanType.GUARDIAN_SEATS_2024 ||
  planType === PlanType.FOUNDATION_2023 ||
  planType === PlanType.DEVELOPER_2025 ||
  planType === PlanType.FOUNDATION_2025;

export const isPlanWithSSO = (planType: PlanType) =>
  planType === PlanType.STARTER_2021 || planType === PlanType.PROFESSIONAL_2021 || planType === PlanType.PROFESSIONAL;

export const isPlanWithExperimentationMAU = (planType: PlanType) =>
  planType === PlanType.ENTERPRISE ||
  planType === PlanType.ENTERPRISE_2023 ||
  planType === PlanType.FOUNDATION_2023 ||
  planType === PlanType.DEVELOPER_2025 ||
  planType === PlanType.FOUNDATION_2025 ||
  planType === PlanType.GUARDIAN_2024 ||
  planType === PlanType.GUARDIAN_SEATS_2024;

export const isPlanWithAIRuns = (planType: PlanType) =>
  planType === PlanType.ENTERPRISE ||
  planType === PlanType.ENTERPRISE_2023 ||
  planType === PlanType.FOUNDATION_2023 ||
  planType === PlanType.DEVELOPER_2025 ||
  planType === PlanType.FOUNDATION_2025 ||
  planType === PlanType.GUARDIAN_2024 ||
  planType === PlanType.GUARDIAN_SEATS_2024;

export const isPlanWithVegaAI = (planType: PlanType) =>
  planType === PlanType.ENTERPRISE ||
  planType === PlanType.ENTERPRISE_2023 ||
  planType === PlanType.GUARDIAN_2024 ||
  planType === PlanType.GUARDIAN_SEATS_2024 ||
  planType === PlanType.FOUNDATION_2023 ||
  planType === PlanType.DEVELOPER_2025 ||
  planType === PlanType.FOUNDATION_2025;

export type IEapSegment = {
  name: string;
  creationDate: number;
  key: string;
  description?: string;
};

export type IEapSegmentsResponse = IEapSegment[];

/** Per-ticket info for STAMP tickets in general API */
export type StampTicketInfo = Readonly<{
  subject?: string;
  created_at?: string;
}>;

/** STAMP tickets in general API: id -> { subject, created_at, ... } */
export type StampTicketsMap = Readonly<Record<string, StampTicketInfo>>;

/** Per-ticket info for open tickets in general API (TSS-1185) */
export type OpenTicketInfo = Readonly<{
  subject?: string;
  created_at?: string;
  status?: string;
}>;

/** Open tickets in general API: id -> { subject, created_at, status } */
export type OpenTicketsMap = Readonly<Record<string, OpenTicketInfo>>;

/** Single route rate limit (TSS-1181) */
export type RouteRateLimit = Readonly<{
  routes: readonly string[];
  rateLimit: number;
  ruleId?: string;
  ruleName?: string;
  description?: string;
}>;

/** Route rate limits from Catfood flag (TSS-1181) */
export type RouteRateLimitsData = Readonly<{
  routeLimits: readonly RouteRateLimit[];
  defaultReadLimit?: number;
  defaultWriteLimit?: number;
}>;

/** Integration item (TSS-1188) */
export type IntegrationItem = Readonly<{
  name?: string;
  enabled?: boolean;
  [key: string]: unknown;
}>;

/** Zendesk section (normalized flat shape for UI) */
export type GeneralTabZendesk = Readonly<{
  supportTier?: string | null;
  csm?: string | null;
  ae?: string | null;
  tam?: string | null;
  stampTickets?: StampTicketsMap;
  openTickets?: OpenTicketsMap;
  focusAccount?: boolean | null;
}>;

/**
 * Raw response from support-internal vitalstatistix-action-get-general lambda.
 * Zendesk is served flat (supportTier, csm, ae, tam, stampTickets, openTickets) to match UI.
 */
export type GeneralAccountInfoResponseRaw = Readonly<{
  launchdarkly?: Readonly<{
    account?: Readonly<{
      eapSegments?: readonly IEapSegment[];
    }>;
    routeRateLimits?: RouteRateLimitsData;
  }>;
  zendesk?: GeneralTabZendesk;
  integrations?: readonly IntegrationItem[];
  supportEntitlement?: Readonly<Record<string, unknown>> | null;
  ratelimit?: Readonly<Record<string, number | string | boolean>>;
  dataExport?: DataExportSummary | null;
  /**
   * System resource-count limit increases (TSS-1181): the `resource-count-*`
   * limits this account has raised above the flag's live default, computed by
   * the support-internal get-general lambda. Empty when the account has none.
   */
  resourceLimits?: readonly AccountLimitIncrease[];
  /**
   * API rate-limit increases (TSS-1181): the account-scoped grants on the
   * per-route rate-limit flag (matched by accountId), computed by the same
   * lambda. Empty when the account has none.
   */
  rateLimitIncreases?: readonly ApiRateLimitIncrease[];
  frequentContacts?: FrequentContactsSection | null;
}>;

/**
 * Independently-fetchable sections of the get-general response (TSS-1181). Vital
 * fetches one per request so each General-tab section loads/fails on its own.
 */
export type GeneralAccountSection =
  | 'launchdarkly'
  | 'zendesk'
  | 'integrations'
  | 'supportEntitlement'
  | 'dataExport'
  | 'resourceLimits'
  | 'rateLimitIncreases'
  | 'frequentContacts';

/** Normalized general tab data (raw response mapped so eap = generalAccountInfo for UI) */
export type GeneralAccountInfoResponse = Readonly<{
  eap?: readonly IEapSegment[];
  zendesk?: GeneralTabZendesk;
  ratelimit?: Readonly<Record<string, number | string | boolean>>;
  routeRateLimits?: RouteRateLimitsData;
  integrations?: readonly IntegrationItem[];
  supportEntitlement?: Readonly<Record<string, unknown>> | null;
  dataExport?: DataExportSummary | null;
  resourceLimits?: readonly AccountLimitIncrease[];
  rateLimitIncreases?: readonly ApiRateLimitIncrease[];
  frequentContacts?: FrequentContactsSection | null;
}>;

/**
 * A single Data Export destination in the account's data-export summary
 * (TSS-1079), as rolled up by the support-internal get-general lambda from the
 * warehouse (not the live public API).
 */
export type FrequentContactMatchStatus = 'matched_by_id' | 'matched_by_email' | 'not_a_member';

export type FrequentContact = Readonly<{
  requesterEmail: string | null;
  requesterName: string | null;
  zendeskUserId: string | null;
  tickets: number;
  lastTicketAt: string | null;
  lastTicketId: string | null;
  isFrequent: boolean;
  memberId: string | null;
  ldRole: string | null;
  ldEffectiveRole: string | null;
  lastWebappActivity: string | null;
  matchStatus: FrequentContactMatchStatus;
}>;

export type FrequentContactsSection = Readonly<{
  contacts: readonly FrequentContact[];
  meta: Readonly<{
    windowDays: number;
    minTickets: number;
    topN: number;
    dataAsOf: string | null;
  }>;
}>;

export type DataExportDestination = Readonly<{
  environmentId: string | null;
  kind: string | null;
  name: string | null;
}>;

/**
 * The account's Data Export configuration summary (TSS-1079), returned on the
 * general-info response by the support-internal lambda. `isConfigured` is the
 * "data export enabled for the customer" signal.
 */
export type DataExportSummary = Readonly<{
  isConfigured: boolean;
  destinations: readonly DataExportDestination[];
  destinationTypes: readonly string[];
  destinationCount: number;
  environmentsWithDestinations: number;
  configuredFor: Readonly<{
    environment: boolean;
    flags: boolean;
    flagRules: boolean;
  }>;
  eventsLimit: number | null;
  lastActivityDate: string | null;
  asOfDate: string | null;
}>;

/**
 * Human-readable names for Data Export destination kinds (TSS-1079). This is a
 * partial lookup: unknown kinds have no entry, so the value type includes
 * `undefined` and `getDataExportDestinationKindName` falls back to the raw kind.
 */
export const DataExportDestinationKindNames: Readonly<Record<string, string | undefined>> = {
  'google-pubsub': 'Google Cloud Pub/Sub',
  kinesis: 'Amazon Kinesis',
  mparticle: 'mParticle',
  segment: 'Segment',
  'azure-event-hubs': 'Azure Event Hubs',
  snowflake: 'Snowflake',
  'snowflake-v2': 'Snowflake',
  databricks: 'Databricks',
  bigquery: 'Google BigQuery',
  redshift: 'Amazon Redshift',
  clickhouse: 'ClickHouse',
};

/** Maps a destination kind to its display name, falling back to the raw kind. */
export const getDataExportDestinationKindName = (kind: string): string => DataExportDestinationKindNames[kind] ?? kind;
